import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { SupportingMeasures } from '@isophi/mng-api';

import { ChildSpzRecommendationForm, ChildSupportingMeasureForm } from '../../constants/forms';

@Component({
  selector: 'app-child-spz-recommendation-form',
  templateUrl: './child-spz-recommendation-form.component.html',
  styleUrls: ['./child-spz-recommendation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildSpzRecommendationFormComponent implements OnInit {
  @Input() formGroup: FormGroup<ChildSpzRecommendationForm>;
  @Input() allowRemove: boolean;
  @Input() supportingMeasureOptions: SupportingMeasures[];

  @Output() remove: EventEmitter<void> = new EventEmitter<void>();

  private collapsedStates = new Map<number, boolean>();

  ngOnInit(): void {
    // Initialize collapsed states based on actualProvisionEnd values
    this.supportingMeasures.controls.forEach((control, index) => {
      const actualProvisionEnd = control.get('actualProvisionEnd')?.value;
      this.collapsedStates.set(index, !!actualProvisionEnd);
    });
  }

  protected supportedMeasureNameById(measureId: number): string {
    const measure = this.supportingMeasureOptions.find((m) => m.id === measureId);
    return measure ? measure.longName : '';
  }

  get supportingMeasures(): FormArray<FormGroup<ChildSupportingMeasureForm>> {
    return this.formGroup.get('supportingMeasures') as FormArray<FormGroup<ChildSupportingMeasureForm>>;
  }

  protected isCollapsed(index: number): boolean {
    return this.collapsedStates.get(index) ?? false;
  }

  protected toggleCollapse(index: number): void {
    const currentState = this.collapsedStates.get(index) ?? true;
    this.collapsedStates.set(index, !currentState);
  }

  protected addSupportingMeasure(): void {
    const newIndex = this.supportingMeasures.length;
    this.supportingMeasures.push(
      new FormGroup<ChildSupportingMeasureForm>({
        id: new FormControl(null),
        supportingMeasure: new FormControl(null),
        start: new FormControl(null),
        end: new FormControl(null),
        aidPurchaseForm: new FormControl(null),
        isFundingRequired: new FormControl(false),
        actualProvisionStart: new FormControl(null),
        actualProvisionEnd: new FormControl(null),
      })
    );
    this.collapsedStates.set(newIndex, false);
  }

  protected removeRecommendation(): void {
    if (!this.allowRemove) {
      return;
    }
    this.remove.emit();
  }

  protected removeSupportingMeasure(index: number): void {
    if (this.supportingMeasures.length <= 1) {
      return;
    }
    this.supportingMeasures.removeAt(index);

    const newMap = new Map<number, boolean>();
    this.collapsedStates.forEach((value, key) => {
      if (key < index) {
        newMap.set(key, value);
      } else if (key > index) {
        newMap.set(key - 1, value);
      }
    });
    this.collapsedStates = newMap;
  }
}
